<template>
  <div class="store-page">
    <!-- 表单容器，与AddStore完全一致的字段结构 -->
    <el-form
      ref="storeForm"
      :model="form"
      :rules="rules"
      label-width="120px"
      class="store-form"
    >
      <!-- 门店名称（必填，≤8字） -->
      <el-form-item label="门店名称" prop="storeName">
        <el-input
          v-model="form.storeName"
          placeholder="请填写门店名称"
          maxlength="8"
          show-word-limit
        />
      </el-form-item>

      <!-- 门店Logo（必填，单张，1:1，≤100KB） -->
      <el-form-item label="门店Logo" prop="storeLogo">
        <div class="upload-area">
          <el-upload
            ref="uploadLogo"
            class="avatar-uploader"
            action="#"
            :show-file-list="false"
            :on-change="handleLogoChange"
            :before-upload="validateLogo"
            :auto-upload="false"
            :disabled="logoUploadDisabled"
          >
            <img v-if="form.storeLogo" :src="form.storeLogo" class="avatar" />
            <i v-else class="el-icon-plus avatar-uploader-icon"></i>
          </el-upload>
          <div class="el-upload__tip">仅支持JPG/PNG，比例1:1，大小≤100KB</div>
          <el-button
            v-if="form.storeLogo"
            type="danger"
            size="mini"
            @click="removeLogo"
            class="delete-btn"
          >
            删除
          </el-button>
        </div>
      </el-form-item>

      <!-- 联系电话（必填，11位手机号） -->
      <el-form-item label="门店联系电话" prop="contactNumbers">
        <el-input
          v-model="form.contactNumbers"
          placeholder="请填写11位手机号"
          maxlength="11"
          show-word-limit
        />
      </el-form-item>

      <!-- 营业执照（选填，单张，≤100KB） -->
      <el-form-item label="营业执照">
        <div class="upload-area">
          <el-upload
            ref="uploadLicense"
            class="avatar-uploader"
            action="#"
            :show-file-list="false"
            :on-change="handleLicenseChange"
            :before-upload="validateFileSize"
            :auto-upload="false"
            :disabled="licenseUploadDisabled"
          >
            <img
              v-if="form.businessLicense"
              :src="form.businessLicense"
              class="avatar"
            />
            <i v-else class="el-icon-plus avatar-uploader-icon"></i>
          </el-upload>
          <div class="el-upload__tip">仅支持JPG/PNG，大小≤100KB</div>
          <el-button
            v-if="form.businessLicense"
            type="danger"
            size="mini"
            @click="removeLicense"
            class="delete-btn"
          >
            删除
          </el-button>
        </div>
      </el-form-item>

      <!-- 其他证照（选填，最多5张，≤100KB） -->
      <el-form-item label="其他证照">
        <div class="upload-area">
          <el-upload
            action="#"
            list-type="picture-card"
            :file-list="certFileList"
            :on-change="handleCertChange"
            :on-remove="handleCertRemove"
            :before-upload="validateFileSize"
            :auto-upload="false"
            :limit="5"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
          <div class="el-upload__tip">仅支持JPG/PNG，最多5张，大小≤100KB</div>
        </div>
      </el-form-item>

      <!-- 门店地址（仅输入框，限制200字符） -->
      <el-form-item label="门店地址" prop="address">
        <el-input
          v-model="form.address"
          placeholder="请填写详细地址（例：湖北省宜昌市西陵区东苑街道珠海路6号半岛花园2期12号楼404）"
          maxlength="200"
          show-word-limit
          rows="3"
          type="textarea"
        />
      </el-form-item>

      <!-- 操作按钮 -->
      <el-form-item>
        <el-button type="primary" @click="submitForm">确认修改</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
export default {
  name: "EditStore",
  data() {
    return {
      form: {
        storeName: "",
        storeLogo: "",
        contactNumbers: "",
        businessLicense: "",
        otherCertificates: [], // 存储其他证照URL数组
        address: "",
      },
      logoUploadDisabled: false,
      licenseUploadDisabled: false,
      certFileList: [], // 上传组件需要的文件列表（含url和uid）
      rules: {
        // 与AddStore完全一致的表单校验规则
        storeName: [
          { required: true, message: "请填写门店名称", trigger: "blur" },
          { max: 8, message: "门店名称最多8个字符", trigger: "blur" },
        ],
        storeLogo: [
          { required: true, message: "请上传门店Logo", trigger: "change" },
        ],
        contactNumbers: [
          { required: true, message: "请填写联系电话", trigger: "blur" },
          {
            pattern: /^1\d{10}$/,
            message: "请输入有效的11位手机号",
            trigger: "blur",
          },
        ],
        address: [
          { required: true, message: "请填写详细地址", trigger: "blur" },
          { max: 200, message: "详细地址最多200个字符", trigger: "blur" },
        ],
      },
      storeId: "", // 门店ID（从URL参数获取）
    };
  },
  mounted() {
    // 1. 从URL查询参数获取门店ID（单门店场景可省略，多门店必须）
    this.storeId = this.$route.params.storeId;
    // 2. 加载已有门店数据（核心：回显数据）
    this.fetchStoreDetail();
  },
  methods: {
    // 核心：加载门店详情（回显数据）
    async fetchStoreDetail() {
      const token = localStorage.getItem("token");
      if (!token) {
        this.$message.error("请先登录");
        this.$router.push("/MerchantLogin");
        return;
      }
      try {
        // 调用后端接口获取门店数据（单门店场景用/storeinfo，多门店用/store/:id）
        // 后端接口说明：/storeinfo 是 findOne()，返回唯一门店；多门店需新增 GET /store/:id 接口
        const response = await this.$http.get(`/api/store/${this.storeId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.code === 0) {
          const store = response.data.data;
          // 3. 回显表单数据（处理图片路径和其他证照列表）
          this.form = {
            storeName: store.storeName,
            // 图片路径直接使用后端返回值（无需重复加/api，AddStore上传时已加）
            storeLogo: store.storeLogo || "",
            contactNumbers: store.contactNumbers || "",
            businessLicense: store.businessLicense || "",
            otherCertificates: store.otherCertificates || [],
            address: store.address || "",
          };
          // 4. 回显其他证照的上传文件列表（需格式化为[{url, uid}]）
          this.certFileList = this.form.otherCertificates.map((url) => ({
            url,
            uid: `${Date.now()}-${Math.floor(Math.random() * 1000)}`, // 生成唯一ID
          }));
        } else {
          throw new Error(response.data.message || "获取门店详情失败");
        }
      } catch (error) {
        console.error("加载门店详情失败:", error);
        this.$message.error(error.message || "加载门店数据失败，请重试");
        this.$router.push("/StoreManage"); // 失败返回列表页
      }
    },

    // ---------------------- 以下方法与AddStore完全一致 ----------------------
    // Logo上传处理
    async handleLogoChange(file) {
      this.logoUploadDisabled = true;
      try {
        const isValid = await this.validateLogo(file.raw);
        if (!isValid) throw new Error("图片验证失败");
        const imageUrl = await this.uploadImage(file.raw);
        this.form.storeLogo = `/api${imageUrl}`; // 上传时拼接/api（与后端静态资源路径匹配）
        this.$message.success("门店Logo上传成功");
      } catch (error) {
        let errorMsg = "门店Logo上传失败";
        if (error.response) {
          if (error.response.status === 401) {
            errorMsg = "未授权，请重新登录";
            this.$router.push("/MerchantLogin");
          } else if (error.response.status === 413) {
            errorMsg = "图片太大，请选择小于100KB的图片";
          }
        }
        this.$message.error(errorMsg);
        this.$refs.uploadLogo.clearFiles();
      } finally {
        this.logoUploadDisabled = false;
      }
    },

    // 营业执照上传处理
    async handleLicenseChange(file) {
      this.licenseUploadDisabled = true;
      try {
        const imageUrl = await this.uploadImage(file.raw);
        this.form.businessLicense = `/api${imageUrl}`;
        this.$message.success("营业执照上传成功");
      } catch (error) {
        let errorMsg = "营业执照上传失败";
        if (error.response?.status === 401) {
          errorMsg = "未授权，请重新登录";
          this.$router.push("/MerchantLogin");
        }
        this.$message.error(errorMsg);
        this.$refs.uploadLicense.clearFiles();
      } finally {
        this.licenseUploadDisabled = false;
      }
    },

    // 其他证照上传处理
    async handleCertChange(file, fileList) {
      try {
        const imageUrl = await this.uploadImage(file.raw);
        const fullUrl = `/api${imageUrl}`;
        this.form.otherCertificates.push(fullUrl);
        // 更新上传文件列表
        this.certFileList = fileList.map((f) =>
          f.uid === file.uid ? { ...f, url: fullUrl } : f
        );
        this.$message.success("证照上传成功");
      } catch (error) {
        this.$message.error("证照上传失败：" + error.message);
        this.certFileList = fileList.filter((f) => f.uid !== file.uid);
      }
    },

    // 通用图片上传接口（与AddStore一致）
    async uploadImage(file) {
      const formData = new FormData();
      formData.append("image", file);
      const token = localStorage.getItem("token");
      const response = await this.$http.post(
        "/api/store/upload-image",
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data?.success && response.data.imageUrl) {
        return response.data.imageUrl;
      } else {
        throw new Error("图片上传接口返回异常");
      }
    },

    // 提交修改（核心：用PUT请求到/updatestore/:id）
    async submitForm() {
      try {
        // 1. 表单校验
        await this.$refs.storeForm.validate();
        const token = localStorage.getItem("token");
        if (!token) {
          this.$message.error("请先登录");
          this.$router.push("/MerchantLogin");
          return;
        }
        // 2. 调用后端编辑接口（PUT /updatestore/:id）
        const response = await this.$http.put(
          `/api/updatestore/${this.storeId}`, // 单门店场景可简化为 `/api/updatestore/${store._id}`，此处用URL参数的id
          this.form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.code === 0) {
          this.$message.success("门店修改成功！");
          this.$router.push("/StoreManage"); // 返回列表页
        } else {
          throw new Error(response.data.message || "修改失败");
        }
      } catch (error) {
        console.error("提交修改失败:", error);
        if (error.response?.status === 401) {
          this.$message.error("登录已过期，请重新登录");
          this.$router.push("/MerchantLogin");
        } else {
          this.$message.error(error.message || "修改失败，请重试");
        }
      }
    },

    // 其他辅助方法（与AddStore完全一致）
    removeLogo() {
      this.form.storeLogo = "";
      this.$message.info("已删除门店Logo");
    },
    removeLicense() {
      this.form.businessLicense = "";
      this.$message.info("已删除营业执照");
    },
    validateLogo(file) {
      return new Promise((resolve) => {
        if (file.size > 1024 * 100) {
          this.$message.error("Logo大小不能超过100KB");
          resolve(false);
          return;
        }
        const img = new Image();
        img.onload = () => resolve(img.width === img.height);
        img.onerror = () => {
          this.$message.error("图片加载失败，请选择有效的图片文件");
          resolve(false);
        };
        img.src = URL.createObjectURL(file);
      });
    },
    validateFileSize(file) {
      if (file.size > 1024 * 100) {
        this.$message.error("文件大小不能超过100KB");
        return false;
      }
      return true;
    },
    handleCertRemove(file, fileList) {
      const index = this.form.otherCertificates.findIndex(
        (url) => url === file.url
      );
      if (index !== -1) this.form.otherCertificates.splice(index, 1);
      this.certFileList = fileList;
    },
  },
};
</script>

<style scoped>
/* 与AddStore完全一致的样式 */
.store-page {
  padding: 20px;
  background: #fff;
}
.store-form {
  max-width: 600px;
  margin: 0 auto;
}
.upload-area {
  margin-bottom: 15px;
}
.avatar-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 148px;
  height: 148px;
  display: inline-block;
  vertical-align: top;
  margin-right: 10px;
}
.avatar-uploader:hover {
  border-color: #409eff;
}
.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 148px;
  height: 148px;
  line-height: 148px;
  text-align: center;
}
.avatar {
  width: 148px;
  height: 148px;
  display: block;
}
.delete-btn {
  margin-top: 10px;
}
</style>